<template>
  <div width="100%" class="pa-2">
    <div v-if="loading" class="d-flex justify-center">
      <circles-loader></circles-loader>
    </div>
    <div v-else-if="valid" class="pa-0">
      <v-row no-gutters class="mt-0">
        <v-col cols="12" sm="12" md="8">
          <v-card class="pa-2 pl-0" flat tile v-if="!forceClassSelect">
            <v-card flat class="ml-3 mr-3 pa-3 pt-2 pb-0" outlined
              v-if="availableClasses && availableClasses.length > 0">
              <v-list class="pt-0">
                <v-subheader class="mb-0 pa-0">
                  <span class="available-text">
                    {{ $t('message.click_to_select_class') }}
                  </span>
                  <span class="ml-1 mr-1 available-text">●</span>
                  <span class="available-text"><b>{{ availableClasses.length }}</b> {{ availableClasses.length > 1 ?
                    $t('message.availables') :
                    $t('message.available') }}</span>
                  <v-spacer></v-spacer>
                  <v-btn ref="reload_data_btn" small @click="loadClasses(true);" :disabled="loadingClasses"
                    elevation="0" fab color="primary">
                    <v-icon>refresh</v-icon>
                  </v-btn>
                </v-subheader>
                <v-divider></v-divider>
                <div v-if="!loadingClasses" class="class-picker">
                  <v-list-item-group v-model="selectedClassItem" color="primary">
                    <v-list-item class="mb-1" style="min-height:50px" @click="selectClass(c)"
                      v-for="(c, index) in availableClasses" two-line :key="'available_class_' + index">
                      <v-list-item-content class="mt-0 mb-0 pt-0 pb-0">
                        <v-list-item-title>
                          {{ c.className }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <v-icon size="20">schedule</v-icon>
                          {{ numberToString(c.startTime) }} <span v-if="c.endTime">- {{ numberToString(c.endTime)
                            }}</span>
                          <span v-if="c.professor">
                            <span class="ml-2 mr-1">{{ $t('message.with') }}</span>
                            <span class="font-weight-bold">{{ c.professor }}</span>
                          </span>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-if="c.maxCapacity">
                          <v-icon size="20">mdi-lock-outline</v-icon>
                          <span class="ml-1">{{ $t('message.requires_registration') }}</span>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-if="c.location">
                          <v-icon size="20">mdi-map-marker-outline</v-icon>
                          <span class="ml-1">{{ c.location }}</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-avatar v-if="!isSmallerDisplay() && c.professorImage">
                        <ImageDisplay :src="c.professorImage" />
                      </v-list-item-avatar>
                      <v-list-item-action>
                        <v-btn small icon fab>
                          <v-icon v-if="isSelectedClass(c)" color="success">mdi-checkbox-marked-circle-outline</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list-item-group>
                </div>
              </v-list>
              <v-card-text v-if="loadingClasses" class="mt-0 mb-0 pt-0 pb-0">
                <v-skeleton-loader type="list-item-two-line">
                </v-skeleton-loader>
              </v-card-text>
            </v-card>
            <v-card elevation="0" outlined v-else>
              <v-list>
                <v-subheader>
                  <span>
                    {{ $t('message.no_classes') }}
                  </span>
                  <v-spacer></v-spacer>
                  <v-btn small @click="loadClasses(true)" :disabled="loadingClasses" elevation="0" fab color="primary">
                    <v-icon>refresh</v-icon>
                  </v-btn>
                </v-subheader>
              </v-list>
            </v-card>
          </v-card>
          <div class="mt-2">
            <v-tabs-items v-model="tab">
              <v-tabs v-model="tab">
                <v-tab class="ml-2" :key="'members-tab'">
                  {{ $t('message.member_checkin') }}
                </v-tab>

                <v-tab :key="'visitors-tab'">
                  {{ $t('message.visitor_checkin') }}
                </v-tab>
                <v-spacer></v-spacer>
                <v-tab :disabled="true" :key="'clock-tab'">
                  <span v-if="currentTime">
                    <v-icon>mdi-clock</v-icon>
                    {{ $d(currentTime, 'clock', $i18n.locale) }}</span>
                </v-tab>
              </v-tabs>

              <v-tab-item :key="'members-tab'">
                <v-card flat class="mt-2">
                  <div class="text-center">
                    <v-container class="text-h2 pb-0 mb-0" v-if="altCheckin">
                      <v-card-text class="pl-0 pt-0 mt-0 pb-0 mb-0 pr-1">
                        <v-autocomplete ref="members_autocomplete" @focus="workingOnCheckin(true)"
                          @blur="workingOnCheckin(false)" v-model="memberName" :items="members"
                          :loading="loadingMembers" outlined clearable
                          :menu-props="{ closeOnContentClick: true, offsetY: true, top: true }" style="font-size:large"
                          item-text="displayName" item-value="_id" :label="$t('message.type_name')" :autofocus="false"
                          height="70">
                          <template v-slot:item="data">
                            <v-list-item v-bind="data.attrs" :input-value="data.selected"
                              @click="selectMember(data.item)">
                              <v-list-item-avatar class="border-white mb-1 initials-avatar">
                                <ImageDisplay v-if="data.item.userImageSmall" :src="data.item.userImageSmall" />
                                <span v-else class="avatar-initials">{{
                                  getInitials(data.item.displayName) }}</span>
                              </v-list-item-avatar>
                              <v-list-item-content>
                                <v-list-item-title class="text-h6">
                                  {{ data.item.displayName }}
                                </v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-autocomplete>
                      </v-card-text>
                    </v-container>
                    <v-container justify-center v-else>
                      <div class="text-center">
                        <v-container v-if="academy">
                          <div v-if="!portraitMode">
                            <v-row no-gutters>
                              <v-col>
                                <v-btn fab dark color="secondary" @click="clickNumber(1)">
                                  1
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn fab dark color="secondary" @click="clickNumber(2)">
                                  2
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn fab dark color="secondary" @click="clickNumber(3)">
                                  3
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn fab dark color="secondary" @click="clickNumber(4)">
                                  4
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn fab dark color="secondary" @click="clickNumber(5)">
                                  5
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn fab dark color="secondary" @click="clickNumber(6)">
                                  6
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn fab dark color="secondary" @click="clickNumber(7)">
                                  7
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn fab dark color="secondary" @click="clickNumber(8)">
                                  8
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn fab dark color="secondary" @click="clickNumber(9)">
                                  9
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn fab dark color="secondary" @click="clickNumber(0)">
                                  0
                                </v-btn>
                              </v-col>
                            </v-row>
                            <v-card-actions>
                              <v-spacer></v-spacer>
                              <v-btn outlined class="mx-2" block dark color="secondary" @click="clickNumber(-1)">
                                <v-icon>backspace</v-icon>
                              </v-btn>
                              <v-spacer></v-spacer>
                            </v-card-actions>
                          </div>
                          <v-card elevation="0" v-else max-width="300" style="margin:auto">
                            <v-row>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(1)">
                                  1
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(2)">
                                  2
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(3)">
                                  3
                                </v-btn>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(4)">
                                  4
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(5)">
                                  5
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(6)">
                                  6
                                </v-btn>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(7)">
                                  7
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(8)">
                                  8
                                </v-btn>
                              </v-col>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(9)">
                                  9
                                </v-btn>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(0)">
                                  0
                                </v-btn>
                              </v-col>
                              <v-col>
                              </v-col>
                              <v-col>
                                <v-btn class="mx-2" fab dark color="secondary" @click="clickNumber(-1)">
                                  <v-icon>backspace</v-icon>
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-card>
                        </v-container>
                        <div class="text-center text-h3">
                          {{ checkinCode ? checkinCode : $t('message.enter_code') }}
                        </div>
                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <v-btn elevation="0" block large color="primary"
                            :disabled="!validNumber || checkingIn ? true : false" @click="processCheckin()">
                            <v-progress-circular v-if="checkingIn" class="mr-1" indeterminate
                              color="white"></v-progress-circular>
                            CHECKIN
                          </v-btn>
                          <v-spacer></v-spacer>
                        </v-card-actions>
                        <v-card-text v-if="message" style="color:#EC7063" class="d-flex justify-center">
                          <h2><v-icon size="35" color="error" class="mr-1">mdi-cancel</v-icon>{{ message }}</h2>
                        </v-card-text>
                      </div>
                    </v-container>
                    <div class="ma-2">
                      <v-card class="ma-2" v-if="checkedInUser && !isSmallerDisplay()" outlined elevation="0"
                        transition="fade-transition">
                        <v-list>
                          <v-subheader>
                            <span class="text-left">{{ $d(new Date(checkedInUser.date), 'time', $i18n.locale) }}</span>
                            <span class="ml-1 mr-1">●</span>
                            <span class="text-left">{{ checkedInUser.name }}</span>
                            <span class="ml-2 text-caption gray--text" v-if="checkedInUser.alreadyCheckedIn">({{
                              $t('message.already_checked_in') }})</span>
                          </v-subheader>
                          <v-list-item>
                            <v-list-item-avatar class="border-white mb-1 initials-avatar">
                              <ImageDisplay v-if="checkedInUser.userImage" :src="checkedInUser.userImage" />
                              <span v-else class="avatar-initials">{{
                                getInitials(checkedInUser.fName + ' ' + checkedInUser.lName) }}</span>
                            </v-list-item-avatar>
                            <v-list-item-content class="text-left">
                              <v-list-item-title>
                                {{ checkedInUser.fName }} {{ checkedInUser.lName }}
                                <country-flag size="20" :country-code="checkedInUser.countryCode" />
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                <belt v-if="!checkedInUser.notBjjUser" :size="13" :belt="checkedInUser.belt" />
                              </v-list-item-subtitle>
                              <v-list-item-subtitle v-if="checkedInUser.membership && checkedInUser.membership.status">
                                <v-icon
                                  :color="getStatusColor(checkedInUser.membership.status)">confirmation_number</v-icon>
                                <span class="text-h6" v-if="checkedInUser.membership.subscriptionEndDate">
                                  {{ $t('message.expires') }}: {{ $d(new
                                    Date(checkedInUser.membership.subscriptionEndDate),
                                    'month_day_year', $i18n.locale) }}
                                </span>
                                <span class="text-h6" v-else-if="checkedInUser.membership.subscriptionCheckins > 0">
                                  {{ $t('message.expires') }}: {{ checkedInUser.membership.checkins }} /{{
                                    checkedInUser.membership.subscriptionCheckins }} {{
                                    $t('message.checkins') }}
                                </span>
                                <v-icon v-else>restart_alt</v-icon>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                              <v-btn v-if="!checkedInUser.checkout && checkedInUser.allowCheckout"
                                :disabled="checkingOut" fab small color="primary" elevation="0"
                                @click="confirmCheckout = true">
                                <v-icon>logout</v-icon>
                              </v-btn>
                              <v-tooltip v-else-if="checkedInUser.allowCheckout" top>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn v-bind="attrs" v-on="on" fab small color="success" elevation="0">
                                    <v-icon>logout</v-icon>
                                  </v-btn>
                                </template>
                                <span>{{ $t('message.checked_out') }}</span>
                              </v-tooltip>
                            </v-list-item-action>
                            <v-list-item-action v-if="alerts && alerts.length > 0">
                              <v-btn fab small color="error" elevation="0" @click="userAlertsDialog = true">
                                <v-icon>warning_amber</v-icon>
                              </v-btn>
                            </v-list-item-action>
                          </v-list-item>
                        </v-list>
                      </v-card>
                    </div>
                  </div>
                </v-card>
              </v-tab-item>
              <v-tab-item :key="'visitors-tab'">
                <v-card flat class="mt-2">
                  <div class="text-center m-t2">
                    <v-card-actions class="mt-2 pt-0 mb-3">
                      <v-btn large block color="primary" depressed @click="openVisitorCheckinDialog()">
                        {{ $t('message.existing_visitor') }}
                      </v-btn>
                    </v-card-actions>
                    <v-card-actions class="mt-2 pt-0 mb-3">
                      <v-btn large block color="primary" depressed @click="launchWaiver">
                        {{ $t('message.new_visitor') }}
                      </v-btn>
                    </v-card-actions>
                  </div>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </div>
          <div v-if="forceClassSelect" class="text-center mt-4 text-h6 grey--text">
            {{ $t('message.select_name_or_visitor_helper') }}
          </div>
        </v-col>
        <v-col>
          <v-card class="pt-2" flat tile>
            <v-card flat outlined width="100%">
              <v-card-title class="text-subtitle-1 mb-0 pb-0">CHECKINS:</v-card-title>
              <v-list :style="'overflow-y: auto;max-height:' + checkinsPanelHeight" v-if="previousCheckins">
                <template v-for="(i, index) in previousCheckins">
                  <v-card outlined class="ma-2" :key="'previous_checkin_' + index + '_' + i._id">
                    <div class="pa-2 pb-0 grey--text">
                      <span class="text-left">{{ $d(new Date(i.date), 'time', $i18n.locale) }}</span>
                      <span v-if="i.className && i.className != ''" class="ml-1 mr-1">●</span>
                      <span v-if="i.className && i.className != ''" class="text-left font-weight-bold">{{ i.className
                      }}</span>
                    </div>
                    <v-list-item class="mt-0 pt-0">
                      <v-list-item-avatar class="border-white mb-1 initials-avatar">
                        <ImageDisplay v-if="i.image" :src="i.image" />
                        <span v-else class="avatar-initials">{{
                          getInitials(i.userName) }}</span>
                      </v-list-item-avatar>
                      <v-list-item-content class="text-left">
                        <v-list-item-title>
                          {{ i.userName }}
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="i.isBirthday" class="pink--text">
                          <v-icon color="pink" class="mr-1">mdi-cake-variant-outline</v-icon>
                          {{ $t('message.happy_birthday') }}!
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-else>
                          <belt style="margin-left: -10px" v-if="!i.notBjjUser" :size="13" :belt="i.belt" />
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-if="i.isProfessor">
                          <v-icon size="20" color="accent" class="mr-1">mdi-police-badge-outline</v-icon>
                          {{ $t('message.professor') }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-else-if="i.isAssistantProfessor">
                          <v-icon size="20" color="accent" class="mr-1">mdi-account-supervisor-outline</v-icon>
                          {{ $t('message.assistant_professor') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <country-flag v-if="i.nationality" size="20" :country-code="i.nationality" />
                      </v-list-item-action>
                    </v-list-item>
                  </v-card>
                </template>
              </v-list>
            </v-card>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <audio v-if="alarmSound" ref="signal_alert" style="display:none">
      <source :src="alarmSound" type="audio/wav">
    </audio>
    <v-dialog v-model="userAlertsDialog" width="80%">
      <v-card outlined>
        <v-card-text v-if="checkedInUser">
          <v-card-title v-if="hasDocumentAlert" class="mb-0 pb-0 mt-0 pt-0">
            <v-icon absolute right v-if="alerts.length > 0">warning</v-icon>
            <span class="text-h6">{{ $t('message.complete_missing_docs') }}</span>
          </v-card-title>
          <v-card-text class="text-center mt-2">
            <template v-for="(a, index) in alerts">
              <v-btn small elevation="0" v-if="a.type == 'alert_missing_document'"
                @click="goToDocument(checkedInUser._id, a.objectId)" :key="'alert' + index" color="primary"
                class="mr-2 mb-2">
                {{ getMissingButtonText(a.text) }}
              </v-btn>
            </template>
          </v-card-text>
          <v-card-text v-if="extraAlerts > 0" class="text-center">
            <h2 v-if="hasDocumentAlert" class="extra-alerts">{{ $t('message.more_alerts', [extraAlerts]) }}</h2>
            <h2 v-else class="extra-alerts">{{ $t('message.hidden_alerts', [extraAlerts]) }}</h2>
          </v-card-text>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn elevation="0" color="secondary" @click="userAlertsDialog = false">{{ $t('message.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmCheckin">
      <v-card outlined>
        <v-card-title v-if="!checkinUserId">{{ $t('message.your_code') }}<span style="color:gray;margin-left:5px;">{{
          checkinCode }}</span></v-card-title>
        <v-card-title v-else>{{ $t('message.your_name') }}<span style="color:gray;margin-left:5px;">{{ checkinUserName
        }}</span></v-card-title>
        <v-card-title>{{ $t('message.about_to_checkin') }}</v-card-title>
        <v-card-text class="pa-6 text-center">
          <h1 v-if="selectedClassStart">{{ this.selectedClass.toUpperCase() }}</h1>
          <h1 v-else>{{ $t('message.no_class') }}</h1>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn elevation="0" x-large color="primary"
            :disabled="(validNumber || checkinUserId) && !checkingIn ? false : true" @click="checkin()">
            <v-progress-circular v-if="checkingIn" class="mr-1" indeterminate color="white"></v-progress-circular>
            CHECKIN
          </v-btn>
          <v-btn elevation="0" x-large color="secondary" @click="confirmCheckin = false">{{ $t('message.cancel') }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
        <v-card-text v-if="message" class="justify-center">
          <v-alert type="error">{{ message }}</v-alert>
        </v-card-text>
        <v-card-actions v-if="reloadAppButton" class="pa-4">
          <v-spacer></v-spacer>
          <v-btn @click="reloadApp()" small text>{{ $t('message.reload_app') }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmCheckout">
      <v-card outlined v-if="checkedInUser">
        <v-card-title v-if="!checkinUserId">{{ $t('message.checking_out') }}</v-card-title>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn elevation="0" x-large color="primary" :disabled="checkingOut" @click="checkout(checkedInUser)">
            <v-progress-circular class="mr-1" indeterminate color="white" v-if="checkingOut">
            </v-progress-circular>
            {{ $t('message.confirm') }}
          </v-btn>
          <v-btn elevation="0" x-large color="secondary" @click="confirmCheckin = false">{{ $t('message.cancel') }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="blockedCheckinDialog">
      <v-card outlined>
        <v-card-text class="text-center">
          <div>
            <v-icon size="70" color="error">mdi-close-circle-outline</v-icon>
          </div>
          <div>
            <h2>{{ $t('message.not_allowed_checkin') }}</h2>
          </div>
        </v-card-text>
        <v-card-text class="text-center mt-2" v-if="invalidStatus">
          <h1><v-icon size="40">mdi-wallet-membership</v-icon>{{ $t('message.invalid_membership') }}</h1>
        </v-card-text>
        <v-card-text class="text-center mb-1 mt-2" v-else-if="alerts && alerts.length > 0">
          <h2 class="text-subtitle-1 mt-1">{{ $t('message.complete_documents') }}</h2>
          <template v-for="(a, index) in alerts">
            <v-btn small elevation="0" v-if="a.type == 'alert_missing_document' && a.required && !a.isSurvey"
              @click="goToDocument(a.user, a.objectId)" :key="'alert' + index" color="primary" class="mr-2 mb-2">
              {{ getMissingButtonText(a.text) }}
            </v-btn>
          </template>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn elevation="0" :disabled="checkingOut" @click="blockedCheckinDialog = false;">
            {{ $t('message.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="visitorCheckinDialog">
      <v-card outlined>
        <v-card-title v-if="!checkinUserId">{{ $t('message.visitor_checkin') }}</v-card-title>
        <v-card-title>
          <h3>{{ $t('message.about_to_checkin') }}</h3>
        </v-card-title>
        <!-- Under forceClassSelect, class selection happens in the card
             picker right after this dialog, so nothing is selected yet at
             this point - showing "no class" here would be misleading, not
             an actual problem to flag. -->
        <v-card-text class="pa-6 text-center" v-if="!forceClassSelect">
          <h1 v-if="selectedClassStart">{{ this.selectedClass.toUpperCase() }}</h1>
          <h1 v-else>{{ $t('message.no_class') }}</h1>
        </v-card-text>
        <v-card-text>
          <v-autocomplete @focus="workingOnCheckin(true)" @blur="workingOnCheckin(false)"
            :no-data-text="$t('message.visitor_needs_waiver')" v-model="selectedVisitor" :items="visitors"
            :menu-props="{ offsetY: true, top: true }" :loading="loadingVisitors" outlined clearable item-text="name"
            item-value="_id" :label="$t('message.visitor_name')">
          </v-autocomplete>
        </v-card-text>
        <v-card-text v-if="visitorMessage" class="text-center error--text">
          {{ visitorMessage }}
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn elevation="0" x-large color="primary" :disabled="checkingOut || checkingIn ? true : false"
            @click="startVisitorCheckin()">
            <v-progress-circular class="mr-1" indeterminate color="white" v-if="checkingIn">
            </v-progress-circular>
            CHECKIN
          </v-btn>
          <v-btn elevation="0" x-large color="secondary" @click="visitorCheckinDialog = false">{{ $t('message.cancel')
          }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="classPickerDialog" max-width="700" scrollable>
      <v-card outlined style="max-height:90vh;display:flex;flex-direction:column">
        <v-card-title>{{ $t('message.click_to_select_class') }}</v-card-title>
        <v-card-text v-if="loadingClasses" class="text-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-card-text>
        <v-card-text v-else-if="!availableClasses || availableClasses.length == 0" class="text-center">
          {{ $t('message.no_classes') }}
        </v-card-text>
        <v-card-text v-else style="overflow-y:auto;flex:1 1 auto;min-height:0">
          <v-row>
            <v-col cols="6" v-for="(c, index) in availableClasses" :key="'class_card_' + index">
              <v-card outlined :color="isSelectedClass(c) ? 'primary' : ''" :dark="isSelectedClass(c)" class="pa-3"
                style="cursor:pointer;height:100%;position:relative" @click="selectClass(c)">
                <!-- Absolutely positioned so it never affects the flex row's
                     (and therefore the card's) height, regardless of
                     selection state - a sibling in the flow would grow
                     whichever row it sits in every time it appears. -->
                <v-icon v-if="isSelectedClass(c)" :dark="isSelectedClass(c)"
                  style="position:absolute;bottom:8px;right:8px">mdi-checkbox-marked-circle-outline</v-icon>
                <div class="d-flex align-center">
                  <div class="text-h6">{{ c.className }}</div>
                  <v-spacer></v-spacer>
                  <v-avatar v-if="c.professorImage" size="56">
                    <ImageDisplay :src="c.professorImage" />
                  </v-avatar>
                </div>
                <div>
                  <v-icon size="18" :dark="isSelectedClass(c)">schedule</v-icon>
                  {{ numberToString(c.startTime) }} <span v-if="c.endTime">- {{ numberToString(c.endTime) }}</span>
                </div>
                <div v-if="c.professor">
                  <v-icon size="18" class="mr-1" :dark="isSelectedClass(c)">mdi-police-badge-outline</v-icon>
                  <span class="font-weight-bold">{{ c.professor }}</span>
                </div>
                <div v-if="c.maxCapacity">
                  <v-icon size="18" :dark="isSelectedClass(c)">mdi-lock-outline</v-icon>
                  {{ $t('message.requires_registration') }}
                </div>
                <div v-if="c.location">
                  <v-icon size="18" :dark="isSelectedClass(c)">mdi-map-marker-outline</v-icon>
                  {{ c.location }}
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text v-if="pickerPersonName" class="d-flex align-center justify-center">
          <v-avatar v-if="pickerPersonImage" size="48" class="mr-2">
            <ImageDisplay :src="pickerPersonImage" />
          </v-avatar>
          <div class="text-h6">{{ pickerPersonName }}</div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn elevation="0" x-large color="primary"
            :disabled="(selectedAcademyClassId || selectedScheduleClassId) && !checkingIn ? false : true"
            @click="confirmClassPicker()">
            <v-progress-circular v-if="checkingIn" class="mr-1" indeterminate color="white"></v-progress-circular>
            CHECKIN
          </v-btn>
          <v-btn elevation="0" x-large color="secondary" @click="cancelClassPicker()">{{ $t('message.cancel') }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
        <v-card-text v-if="message" class="justify-center">
          <v-alert type="error">{{ message }}</v-alert>
        </v-card-text>
        <v-card-text v-if="visitorMessage" class="text-center error--text">
          {{ visitorMessage }}
        </v-card-text>
        <v-card-actions v-if="reloadAppButton" class="pa-4">
          <v-spacer></v-spacer>
          <v-btn @click="reloadApp()" small text>{{ $t('message.reload_app') }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="notAllowedDialog">
      <v-card outlined>
        <v-card-text v-if="message" class="text-center error--text pa-2">
          <h2><v-icon size="35" color="error" class="mr-1">mdi-cancel</v-icon>{{ message }}</h2>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn elevation="0" x-large color="primary" @click="notAllowedDialog = false; message = null">
            OK
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="codeConfirmation">
      <v-card class="pa-2">
        <v-card-text class="text-center text-h6">
          <h3>{{ $t('message.your_checkin_code') }} </h3>
        </v-card-text>
        <v-card-text class="text-center text-h1">
          <h1><b>{{ newMemberCode }}</b></h1>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn elevation="0" x-large color="primary" @click="codeConfirmation = false">
            OK
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Belt from '@/components/displays/BeltDisplay.vue';
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import { default as CountryDisplay, default as CountryFlag } from '@/components/displays/CountryFlagDisplay.vue';
import Api from "@/services/Api";

export default {
  name: 'Checkin',
  data() {
    return {
      memberId: this.$route.query.m,
      newMemberCode: this.$route.query.code,
      newMemberId: this.$route.query.id,
      loading: true,
      tab: null,
      loadingMembers: true,
      loadingClasses: false,
      validNumber: false,
      memberName: null,
      members: [],
      valid: true,
      message: null,
      reloadAppButton: false,
      visitorMessage: null,
      academy: null,
      hasProducts: false,
      checkedInUser: null,
      showConfirm: false,
      selectedClassItem: null,
      selectedClass: 0,
      altCheckin: false,
      selectedAcademyClassId: null,
      selectedScheduleClassId: null,
      availableClasses: null,
      allClasses: null,
      selectedClassStart: null,
      checkinCode: '',
      checkinUserId: null,
      checkinUserName: null,
      skipAutoReloads: false,
      alerts: null,
      blockedCheckinDialog: false,
      extraAlerts: 0,
      userAlertsDialog: false,
      confirmCheckin: false,
      forceClassSelect: false,
      classPickerDialog: false,
      classPickerContext: null,
      hasDocumentAlert: false,
      invalidStatus: false,
      checkingIn: false,
      checkingOut: false,
      confirmCheckout: null,
      classProfessorId: null,
      assistantProfessors: [],
      visitorCheckinDialog: false,
      blockCheckins: false,
      requiredDocuments: [],
      selectedVisitor: null,
      visitors: [],
      loadingVisitors: false,
      portraitMode: false,
      codeConfirmation: null,
      alarmSound: null,
      skipInterval: null,
      previousCheckins: null,
      notAllowedDialog: null,
      currentTime: new Date(),
      clockTimer: null,
      checkinsPanelHeight: '400px'
    }
  },
  beforeDestroy() {
    if (this.skipInterval)
      clearInterval(this.skipInterval);
    if (this.clockTimer)
      clearInterval(this.clockTimer);
  },
  created() {
    //Start interval every 5 min, 
    //1- send back to main tab
    //2- refresh classes and members
    //3- Add focus out so we can continue and reactivate timer
    const vm = this;
    this.skipInterval = setInterval(function () {
      if (!vm.skipAutoReloads) {
        vm.getVisitors();
        vm.getMembers(); if (!vm.forceClassSelect) {
          vm.loadClasses();
        }
        vm.tab = 0;
      }
      vm.getPreviousCheckins();
    }, 600000);
    //Calculate the side panel height 

    if (this.isSmallerDisplay())
      this.checkinsPanelHeight = (window.innerHeight - 630) + "px";
    else
      this.checkinsPanelHeight = (window.innerHeight - 200) + "px";
    //console.log('checkinsPanelHeight:', this.checkinsPanelHeight);
    this.alarmSound = this.assets('sounds/signal_alert.wav')
    if (window.innerHeight > window.innerWidth) {
      this.portraitMode = true;
    }
    if (this.memberId) {
      this.getMember(this.memberId);
    }
    Api().get('admintablet/academy/' + this.$store.state.tabletManagedAcademy + '/has/products')
      .then(response => {
        const data = response.data;
        if (data) {
          this.hasProducts = data.hasProducts;
        }
      });
    Api().get('admintablet/academy/' + this.$store.state.tabletManagedAcademy)
      .then(response => {
        const data = response.data;
        if (data) {
          if (data.configuration) {
            this.altCheckin = data.configuration.altTabletCheckin;
            this.blockCheckins = data.configuration.blockMissingDocCheckins;
            this.showConfirm = data.configuration.tabletCheckinConfirm;
            this.forceClassSelect = data.configuration.forceClassSelectForTabletCheckins;
            if (this.altCheckin) {
              this.getMembers();
            } else if (this.newMemberCode && this.newMemberCode != "") {
              this.codeConfirmation = true;
            }
          }
          this.academy = data;
          this.checkValidity();
          const preferredLanguage = data.preferredLanguage ? data.preferredLanguage.toLowerCase() : 'en';
          this.setLang(preferredLanguage);
          this.loadClasses();
          this.getPreviousCheckins();
          this.loading = false;
        }
      })
      .catch(e => {
        if (e) {
          console.log('Error loading checkin page: ', e);
        }
      })
  },
  mounted() {
    if (!this.$store.state.tabletManagedAcademy) {
      this.logout();
    }
    this.clockTimer = setInterval(this.updateTime, 1000);
  },
  components: {
    CirclesLoader,
    CountryDisplay,
    CountryFlag,
    Belt
  },
  watch: {
    checkinCode: {
      immediate: true,
      handler: function () {
        this.checkCodeValidity()
      }
    }
  },
  computed: {
    // Resolves who's about to check in for the class-picker dialog's
    // identity row - the only info available differs by how they got here:
    // search mode already has a member record (name + maybe a picture),
    // PIN mode only has the entered code (no name known client-side until
    // the server responds), and visitors only ever have a name (the
    // /visitors endpoint doesn't return an image at all).
    pickerPersonName() {
      if (this.classPickerContext == 'visitor') {
        const v = this.visitors.find((x) => x._id == this.selectedVisitor);
        return v ? v.name : null;
      }
      if (this.checkinUserId) return this.checkinUserName;
      if (this.checkinCode) return this.checkinCode;
      return null;
    },
    pickerPersonImage() {
      if (this.classPickerContext == 'visitor') return null;
      if (this.checkinUserId) {
        const m = this.members.find((x) => x._id == this.checkinUserId);
        return m ? m.userImageSmall : null;
      }
      return null;
    },
  },
  methods: {
    async reloadApp() {
      const currentVersion = this.appVersion;
      const response = await Api()
        .get("utils/version/bjjlink-kiosk/latest?patch=true");
      const backendVersion = response.data;
      if (backendVersion && backendVersion.version && backendVersion.version != currentVersion) {
        console.log('Not current version[' + currentVersion + ' vs ' + backendVersion.version + ']! Reloading entire app');
        const url = new URL(window.location.href);
        url.searchParams.set('v', backendVersion.version);
        window.location.replace(url.toString());
      } else {
        window.location.reload(true);
      }
    },
    launchWaiver() {
      //console.log('scheduleClassId: ', this.selectedScheduleClassId)
      if (this.selectedScheduleClassId)
        this.navigateTo({ name: 'waiver', query: { scid: this.selectedScheduleClassId } });
      else if (this.selectedAcademyClassId)
        this.navigateTo({ name: 'waiver', query: { acid: this.selectedAcademyClassId } });
      else
        this.navigateTo({ name: 'waiver' });
    },
    updateTime() {
      this.currentTime = new Date();
    },
    isSelectedClass(c) {
      if (this.selectedScheduleClassId && c.scheduleClassId == this.selectedScheduleClassId)
        return true;
      else if (this.selectedAcademyClassId && c.academyClassId == this.selectedAcademyClassId) {
        return true;
      }
      return false;
    },
    workingOnCheckin(val) {
      this.skipAutoReloads = val;
    },
    visitorCheckin() {
      this.ensureClassSelected();
      if (this.selectedVisitor) {
        this.visitorMessage = null;
        const date = new Date();
        const firstHour = new Date();
        firstHour.setHours(0);
        firstHour.setMinutes(0);
        const postData = {
          date: date.toString(),
          firstHour: firstHour,
          day: this.getDayString(date),
          academyId: this.$store.state.tabletSelectedAcademy,
          academyClassId: this.selectedAcademyClassId,
          scheduleClassId: this.selectedScheduleClassId,
          professorId: this.classProfessorId,
          assistantProfessors: this.assistantProfessors,
          className: this.selectedClassStart ? this.selectedClass : '',
          startTime: this.selectedClassStart
        };
        this.checkingIn = true;
        Api()
          .post(
            "admintablet/academy/" +
            this.$store.state.tabletManagedAcademy +
            "/checkin/visitor/" + this.selectedVisitor, postData
          )
          .then((res) => {
            this.checkingIn = false;
            this.visitorCheckinDialog = false;
            this.classPickerDialog = false;
            this.classPickerContext = null;
            this.selectedVisitor = null;
            this.getVisitors();
            this.getMembers();
            this.loadClasses();
            this.getPreviousCheckins();
            this.tab = 0;
            this.skipAutoReloads = false;
            let data = res.data;
            if (!data.data.alreadyCheckedIn)
              this.checkedInUser = data.data;
          })
          .catch((e) => {
            // Same idea as checkin()'s catch - stay open so the person can
            // just retry instead of losing their visitor/class selection.
            this.checkingIn = false;
            if (e && e.response && e.response.data && e.response.data.localizedMessage) {
              this.visitorMessage = this.$t('message.' + e.response.data.localizedMessage);
            } else {
              this.visitorMessage = this.$t('message.error_try_again');
            }
          });
      } else {
        this.visitorMessage = this.$t('message.select_visitor');
      }
    },
    getPreviousCheckins() {
      const day = new Date();
      day.setHours(1);
      day.setMinutes(0);
      Api()
        .get(
          "admintablet/academy/" +
          this.$store.state.tabletManagedAcademy +
          "/checkins?day=" + day.toString()
        )
        .then(response => {
          if (response.data.checkins) {
            for (let i of response.data.checkins) {
              i.isBirthday = this.isBirthday(i.dob);
            }
          }
          this.previousCheckins = response.data.checkins;
          //No previous checkins we might be on a new day so just clear the last one
          if (!this.previousCheckins || !this.previousCheckins.length) {
            this.checkedInUser = null;
          }
        });
    },
    openVisitorCheckinDialog() {
      this.getVisitors();
      this.ensureClassSelected();
      this.visitorCheckinDialog = true;
    },
    getVisitors() {
      this.loadingVisitors = true;
      Api()
        .get(
          "admintablet/academy/" +
          this.$store.state.tabletManagedAcademy +
          "/visitors"
        )
        .then(response => {
          this.visitors = response.data.visitors;
          this.loadingVisitors = false;
        });
    },
    getRequiredDocuments() {
      Api()
        .get(
          "admintablet/academy/" +
          this.$store.state.tabletManagedAcademy +
          "/required-documents"
        )
        .then((response) => {
          this.requiredDocuments = response.data.requiredDocuments;
        });
    },
    numberToString(time) {
      if (!time)
        return "";
      if (!isNaN(time)) {
        const split = parseFloat(time).toFixed(2).split(".");
        let hours = parseInt(split[0]);
        const minutes = split[1];
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        return hours + ":" + minutes + " " + ampm;
      }
    },
    selectMember(member) {
      this.checkinUserId = member._id;
      this.checkinUserName = member.displayName;
      this.processCheckin();
    },
    getMembers() {
      Api()
        .post(
          "admintablet/academy/" +
          this.$store.state.tabletManagedAcademy +
          "/users?source=members"
        )
        .then(response => {
          this.members = response.data.users;
          this.loadingMembers = false;

          if (this.newMemberId && this.newMemberId != "") {
            for (const m of this.members) {
              if (m._id == this.newMemberId) {
                this.memberName = this.newMemberId;
                this.selectMember(m);
                break;
              }
            }
          }
        });
    },
    logout() {
      this.$store.dispatch("setToken", null);
      this.$store.dispatch("setTabletId", null);
      this.$store.dispatch("setTabletSelectedAcademy", null);
      this.$store.dispatch("setTabletManagedAcademy", null);
      localStorage.removeItem("tablet_token");
      window.location = 'login';
    },
    goToDocument(memberId, templateId) {
      this.navigateTo({ name: 'template', params: { templateId: templateId, memberId: memberId } })
    },
    getMissingButtonText(text) {
      var toReturn = '';
      if (text) {
        if (text.length < 32) {
          toReturn = text;
        } else {
          toReturn = text.substring(0, 32) + '...';
        }
      }
      return toReturn;
    },
    getMember(memberId) {
      Api().get('admintablet/academy/' + this.$store.state.tabletManagedAcademy + '/member/' + memberId)
        .then(response => {
          const data = response.data;
          if (data) {
            this.checkedInUser = {
              _id: data.member._id,
              date: data.date,
              allowCheckout: data.allowCheckout,
              alreadyCheckedIn: data.alreadyCheckedIn,
              fName: data.member.fName,
              lName: data.member.lName,
              belt: data.member.belt,
              userImage: data.member.userImageSmall,
              notBjjUser: data.member.notBjjUser,
              countryCode: data.member.nationality,
              member: true,
              name: data.name,
              checkout: data.checkout,
              checkinId: data._id
            };
            this.alerts = data.alerts;
          }
        });
    },
    checkValidity() {
      if (this.selectedAcademy) {
        this.valid = true;
      }
    },
    clickNumber(num) {
      if (num < 0) {
        if (this.checkinCode.length > 0) {
          //deleting
          this.checkinCode = this.checkinCode.substring(0, this.checkinCode.length - 1);
        }
      } else {
        if (this.checkinCode.length < 6)
          this.checkinCode += num + '';
      }
    },
    selectClass(item) {
      if (item) {
        if (this.allClasses && this.allClasses.length > 1)
          item.showAll = true;
        this.selectedClass = item.className;
        this.selectedClassStart = item.startTime;
        this.classProfessorId = item.professorId;
        this.assistantProfessors = item.assistantProfessors;
        this.selectedAcademyClassId = item.academyClassId;
        this.selectedScheduleClassId = item.scheduleClassId;
        //this.availableClasses = [item];
      }
    },
    selectFirstAvailable() {
      if (this.availableClasses.length >= 1) {
        this.selectedClass = this.availableClasses[0].className;
        this.classProfessorId = this.availableClasses[0].professorId;
        this.assistantProfessors = this.availableClasses[0].assistantProfessors;
        this.selectedClassStart = this.availableClasses[0].startTime;
        this.selectedClassItem = 0;
        if (this.availableClasses[0] && this.availableClasses[0].academyClassId) {
          this.selectedAcademyClassId = this.availableClasses[0].academyClassId;
        }
        if (this.availableClasses[0] && this.availableClasses[0].scheduleClassId) {
          this.selectedScheduleClassId = this.availableClasses[0].scheduleClassId;
        }
      }
    },
    // Safety net for a real gap: loadClasses() only (re)runs its preselection
    // on its own schedule (page load, the 10-min interval, after a checkin
    // completes) - if a checkin is attempted in the window before the very
    // first classes fetch resolves (or after some other reason left the
    // selection blank), selectedClassStart can still be falsy even though
    // availableClasses is non-empty, which is what was showing "no class" to
    // visitors despite a class genuinely being available. Never applies under
    // forceClassSelect - there, a blank selection until the person actively
    // taps a card is the intended behavior, not a gap to paper over.
    ensureClassSelected() {
      if (this.forceClassSelect) return;
      if (!this.selectedClassStart && this.availableClasses && this.availableClasses.length > 0) {
        this.selectFirstAvailable();
      }
    },
    loadClasses(loadMembers) {
      this.loadingClasses = true;
      const now = new Date();
      Api().get('admintablet/academy/' + this.$store.state.tabletManagedAcademy + '/classes?now=' + now.toString(), { retries: true })
        .then(response => {
          this.availableClasses = response.data;
          this.allClasses = response.data;
          // When forceClassSelectForTabletCheckins is on, nothing should be
          // preselected - the whole point is making the person actively tap
          // a class card, instead of silently inheriting whichever class is
          // "closest to now" (the exact bug this setting exists to fix).
          if (this.forceClassSelect) {
            //Do nothing
          } else if (this.availableClasses.length == 1) {
            this.selectedClass = this.availableClasses[0].className;
            this.selectedClassStart = this.availableClasses[0].startTime;
            this.classProfessorId = this.availableClasses[0].professorId;
            this.assistantProfessors = this.availableClasses[0].assistantProfessors;
            this.selectedClassItem = 0;
            if (this.availableClasses[0] && this.availableClasses[0].academyClassId) {
              this.selectedAcademyClassId = this.availableClasses[0].academyClassId;
            }
            if (this.availableClasses[0] && this.availableClasses[0].scheduleClassId) {
              this.selectedScheduleClassId = this.availableClasses[0].scheduleClassId;
            }
          } else if (this.availableClasses.length > 1) {
            const now = new Date();
            const nowHour = now.getHours();
            let classCount = 0;
            let closestClass = null;
            let closestTimeDifference = 24;
            let closestClassCount = 0;
            for (const c of this.availableClasses) {
              const classHour = Math.floor(c.startTime);
              const currentTimeDifference = nowHour - classHour;
              if (!closestClass || (currentTimeDifference <= 0 && currentTimeDifference > closestTimeDifference)) {
                closestTimeDifference = currentTimeDifference;
                closestClass = c;
                closestClassCount = classCount;
              }
              classCount++;
            }
            if (closestClass) {
              this.selectedClass = closestClass.className;
              this.selectedClassStart = closestClass.startTime;
              this.classProfessorId = closestClass.professorId;
              this.assistantProfessors = closestClass.assistantProfessors;
              this.selectedClassItem = closestClassCount;
              if (closestClass.academyClassId) {
                this.selectedAcademyClassId = closestClass.academyClassId;
              }
              if (closestClass.scheduleClassId) {
                this.selectedScheduleClassId = closestClass.scheduleClassId;
              }
            }
          }
          this.loadingClasses = false;
        })
        .catch(e => {
          if (e)
            this.loadingClasses = false;
        });
      if (loadMembers) {
        this.getVisitors();
        this.getMembers();
        this.getPreviousCheckins();
      }
      if (this.blockCheckins) {
        this.getRequiredDocuments();
      }
    },
    checkCodeValidity() {
      if (this.checkinCode && this.checkinCode.length == 6)
        this.validNumber = true;
      else
        this.validNumber = false;
    },
    launchProducts() {
      this.navigateTo({ name: 'products' });
    },
    processCheckin() {
      this.message = null;
      this.ensureClassSelected();
      if (this.forceClassSelect)
        // tabletCheckinConfirm's dialog is a separate, plain "are you sure"
        // step - it's not needed (and is intentionally skipped) once class
        // selection is itself the deliberate confirmation step.
        this.openClassPicker('member');
      else if (!this.showConfirm)
        this.checkin();
      else
        this.confirmCheckin = true;
    },
    openClassPicker(context) {
      // Always start blank - a leftover selection from a previous person's
      // checkin (or the pre-forceClassSelect auto-preselect logic) must never
      // carry over and look like a default choice here.
      this.selectedClassItem = null;
      this.selectedClass = 0;
      this.selectedClassStart = null;
      this.selectedAcademyClassId = null;
      this.selectedScheduleClassId = null;
      this.classProfessorId = null;
      this.assistantProfessors = [];
      this.message = null;
      this.visitorMessage = null;
      this.reloadAppButton = false;
      this.classPickerContext = context;
      this.classPickerDialog = true;
      // The always-visible class list (and its "reload" button) is gone in
      // this mode, so this is the only remaining moment classes get
      // refreshed - fetch fresh right as the picker opens instead of
      // relying on whatever loadClasses() happened to fetch last (page
      // load, or the 10-min background interval), which could already be
      // stale for a class that started/ended in between.
      this.loadClasses();
    },
    cancelClassPicker() {
      this.classPickerDialog = false;
      if (this.classPickerContext == 'visitor')
        this.visitorCheckinDialog = true;
      this.classPickerContext = null;
    },
    confirmClassPicker() {
      // Deliberately NOT closing classPickerDialog here - it should only
      // close once checkin()/visitorCheckin() actually gets a response back,
      // same as the old confirmCheckin dialog. Closing it eagerly would mean
      // a failed attempt (blocked checkin, network error) drops the person
      // straight back to square one, losing the class they just picked,
      // instead of letting them see what went wrong and just retry.
      if (this.classPickerContext == 'visitor')
        this.visitorCheckin();
      else
        this.checkin();
    },
    startVisitorCheckin() {
      if (!this.selectedVisitor) {
        this.visitorMessage = this.$t('message.select_visitor');
        return;
      }
      if (this.forceClassSelect) {
        this.visitorCheckinDialog = false;
        this.openClassPicker('visitor');
      } else {
        this.visitorCheckin();
      }
    },
    playSound() {
      const sound = this.$refs['signal_alert'];
      sound.volume = 1.0;
      sound.play();
    },
    checkin() {
      this.ensureClassSelected();
      this.reloadAppButton = false;
      this.checkingIn = true;
      this.invalidStatus = false;
      this.alerts = null;
      this.message = null;
      const date = new Date();
      const firstHour = new Date();
      firstHour.setHours(0);
      firstHour.setMinutes(0);
      const postData = {
        date: date.toString(),
        firstHour: firstHour,
        userId: this.checkinUserId,
        day: this.getDayString(date),
        academyId: this.$store.state.tabletSelectedAcademy,
        professorId: this.classProfessorId,
        assistantProfessors: this.assistantProfessors,
        academyClassId: this.selectedAcademyClassId,
        scheduleClassId: this.selectedScheduleClassId,
        className: this.selectedClassStart ? this.selectedClass : '',
        startTime: this.selectedClassStart,
        requiredDocuments: this.blockCheckins ? this.requiredDocuments : []
      };
      //var code = this.checkinCode.replace(/-/g,'');
      let code = this.checkinCode;
      if (!code)
        code = this.checkinUserId;
      if (!code) {
        this.message = this.$t('Error');
        return;
      }
      this.checkedInUser = null;
      this.message = null;
      Api().post('admintablet/academy/' + this.$store.state.tabletManagedAcademy + '/checkin/' + code, postData, { retries: true })
        .then(response => {
          const data = response.data;
          if (data) {
            try {
              this.checkinCode = '';
              this.confirmCheckin = false;
              this.classPickerDialog = false;
              this.classPickerContext = null;
              this.checkingIn = false;
              this.alerts = data.alerts;
              /**CLARK ONLY **/
              //Do nto remove IF or everyone gets the annoying sound
              const vm = this;
              if (this.$store.state.tabletManagedAcademy == '5d9432d371b07c6c172e8e8e') {
                if (!data.membership || data.membership.status != 'active') {
                  vm.playSound();
                }
              }

              /** CLARK ONLY END */
              if (data.blockedCheckin) {
                //data.missingRequiredDocs.length > 0 && this.alerts && this.alerts.length > 0
                this.blockedCheckinDialog = true;
                this.invalidStatus = data.invalidStatus;
              } else if (!data.alreadyCheckedIn) {
                this.checkedInUser = {
                  _id: data.member._id,
                  fName: data.member.fName,
                  lName: data.member.lName,
                  belt: data.member.belt,
                  date: data.date,
                  alreadyCheckedIn: data.alreadyCheckedIn,
                  allowCheckout: data.allowCheckout,
                  userImage: data.member.userImageSmall,
                  countryCode: data.member.nationality,
                  notBjjUser: data.member.notBjjUser,
                  daysAtBelt: data.daysAtBelt,
                  classesAtBelt: data.classesAtBelt,
                  member: true,
                  name: data.name,
                  membership: data.membership,
                  checkout: data.checkout,
                  checkinId: data._id
                };
              }
              if (this.alerts && this.alerts.length > 0) {
                for (const a of this.alerts) {//Flag subscription alerts differently
                  if (a.type == 'alert_missing_document' && !a.isSurvey)
                    this.hasDocumentAlert = true;
                  else
                    this.extraAlerts++;
                }
              }
              this.checkinUserId = null;
              this.checkinUserName = null;
              this.skipAutoReloads = false;
              const reloadBtnEl = this.$refs['reload_data_btn'];
              const membersAutocompleteEl = this.$refs['members_autocomplete'];
              this.getPreviousCheckins();
              // Blurring the member search field is what makes the tablet's
              // touch keyboard go away after a checkin - this must not
              // depend on reload_data_btn existing, since that button isn't
              // rendered at all under forceClassSelect (no always-visible
              // class list there), which was silently skipping the blur too.
              if (membersAutocompleteEl) {
                setTimeout(function () {
                  membersAutocompleteEl.blur();
                  if (reloadBtnEl) reloadBtnEl.click();
                }, 1000);
              }
            } catch (e) {
              this.checkingIn = false;
              this.confirmCheckin = false;
              this.classPickerDialog = false;
              this.classPickerContext = null;
              console.log(e);
              this.message = this.$t('message.error_generic');
            }
          }
        })
        .catch(e => {
          // Deliberately not closing confirmCheckin/classPickerDialog here -
          // the request itself failed (network error, blocked/invalid
          // response), so the person should stay right where they were and
          // just be able to retry instead of losing their class selection
          // and having to re-identify from scratch.
          if (e && e.response && e.response.data && e.response.data.localizedMessage) {
            this.message = this.$t('message.' + e.response.data.localizedMessage);
            this.notAllowedDialog = true;
          } else {
            this.message = this.$t('message.error_try_again');
            this.reloadAppButton = true;
          }
          this.checkingIn = false;
        })
    },
    checkout() {
      const time = new Date();
      this.checkingOut = true;
      const postData = {
        time: time,
        userId: this.checkedInUser._id,
        code: this.checkinCode,
      };
      Api().patch('admintablet/academy/' + this.$store.state.tabletManagedAcademy + '/checkout/' + this.checkedInUser.checkinId, postData)
        .then(response => {
          const data = response.data;
          if (data) {
            this.checkingOut = false;
            this.checkedInUser.checkout = time;
            this.confirmCheckout = false;
          }
        })
        .catch(() => {
          this.checkingOut = false;
        })
    }
  },
}
</script>
<style>
.class-picker {
  overflow-y: auto;
  max-height: 165px;
}

.rotate {
  animation: rotation 2s infinite linear;
}

.initials-avatar {
  background-color: #eee !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  /* Add this border */
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  box-sizing: border-box;
  /* Ensures the border doesn't increase the avatar size */
}

/* If you want the image avatars to have the same look for consistency */
.initials-avatar ::v-deep img {
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.avatar-initials {
  font-size: 1vw;
  font-weight: bold;
  color: #555;
}

@media only screen and (max-width: 541px) {
  .available-text {
    font-size: 0.775em;
  }
}

@media only screen and (min-width: 542px) {
  .v-dialog {
    width: 50% !important;
  }
}
</style>